import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../ui/icon/icon';
import EventImage from '../../../../picture/event-image.png';
import HtmlContent from '../../../htmlContent';
import i18n from 'i18next';
import './eventCard.css';
import moment from 'moment';
import { classnames } from '../../../../utils/classnames';

export function ImageEvent({ t, locale, config, eventIsOver, saleIsOver, eventComingSoon }) {
    const { full_picture, sale_start_date } = config.event_header;

    const coverText = React.useMemo(() => {
        switch(true) {
            case eventIsOver:
                return t('widget.event-card.over');
            case saleIsOver:
                return t('widget.event-card.sale-over');
            case eventComingSoon:
                return <div>
                    <div className='neo-event-card-image-cover-text-coming-soon'>
                        {t('widget.event-card.coming-soon')}
                    </div>
                    <div>
                        <div className='neo-event-card-cover-date-day'>
                            {new Intl.DateTimeFormat(locale, {day: '2-digit', month: 'short', year:'numeric'}).format(new Date (moment.utc(sale_start_date, moment.ISO_8601)))}
                        </div>
                        <div className='neo-event-card-date-hour'>
                            {new Intl.DateTimeFormat(locale, {hour: '2-digit', minute: '2-digit'}).format(new Date (moment.utc(sale_start_date, moment.ISO_8601)))}
                        </div>
                    </div>
                </div>
            default:
                return '';
        }
    }, [t, eventComingSoon, saleIsOver, eventIsOver, sale_start_date, locale]);

    return (
        <div style={{position: 'relative'}}>
            {(eventIsOver || saleIsOver || eventComingSoon) && (
                <div className={classnames('neo-event-card-image-cover', {'black-cover': eventIsOver || saleIsOver})}>
                    <div className='neo-event-card-image-cover-text'>
                        {coverText}
                    </div>
                </div> 
            )}
            <div className='neo-event-card-image'>
                <img alt='logo' src={full_picture || EventImage}/>
            </div>
        </div>
    );
}

function DateInformation({ t, locale, config, settings }) {
    const { start_date, end_date } = config.event_header;
    const { display_hours = true } = settings;

    return (
        <div className='neo-info-group-with-icon'>
            <Icon name='calendar'/>
            <div className='neo-dates'>
                {start_date && <div>
                    {t('widget.start')}
                    {' : '}
                    {moment(start_date).format(moment.localeData().longDateFormat('L'))}
                    {display_hours && <>
                        {` ${t('widget.at')} `}
                        {new Date (start_date).toLocaleString(locale, {timeZone: config.event_header.timezone, hour: '2-digit', minute: '2-digit'})}
                    </>}
                </div>}
                {end_date && <div>
                    {t('widget.end')}
                    {' : '}
                    {moment(end_date).format(moment.localeData().longDateFormat('L'))}
                    {display_hours && <>
                        {` ${t('widget.at')} `}
                        {new Date (end_date).toLocaleString(locale, {timeZone: config.event_header.timezone, hour: '2-digit', minute: '2-digit'})}
                    </>}
                </div>}
            </div>
        </div>
    );
}

function InfosEvent({
    t,
    locale,
    config,
    settings,
    eventComingSoon,
    eventIsOver,
    saleIsOver,
    hasDescription,
    showMore,
    toggleShowMore
}) {
    const { name, address, start_date } = config.event_header;
    const {
        display_date = true,
        display_location = true,
        display_name_location = true,
        display_description = true,
        display_hours = true,
    } = settings;    
    
    const _address = React.useMemo(() => {
        const address_name = display_name_location && address?.name ? address?.name : '';
        const separator = display_name_location && address?.name && address?.city ? ', ' : '';

        if (!display_name_location) {
            return `${address?.address1}, ${address?.city}`
        }
        return `${address_name}${separator}${address?.city}`
       }, [address, display_name_location]);

    const hideShowMoreToggle = React.useMemo(() => {
        return (!hasDescription || !display_description) && !display_location && !display_date;
    }, [hasDescription, display_description, display_location, display_date]);

    return (
        <div className='neo-event-card-info-and-date'>
            <div className='neo-event-card-infos'>
                <div className='neo-event-card-title'>{name}</div>
                {display_location && _address.length > 0 && <div className='neo-event-card-location'>{_address}</div>}
                {!hideShowMoreToggle && (
                    <div className='neo-event-card-read-more'>
                        <div className={`neo-read-more-icon ${showMore ? 'active' : ''}`} onClick={(e) => {e.stopPropagation(); toggleShowMore()}}>
                            <Icon name='infoCircle' className='neo-event-card-info-icon' />
                            <div className='neo-event-card-read-more'>{showMore ? t('widget.resale.event.show-less') : t('widget.resale.event.show-more')}</div>
                        </div>
                    </div>
                )}
            </div>
            {display_date && <div className={classnames('neo-event-card-dates', {'disabled' : eventComingSoon || eventIsOver || saleIsOver})}>
                <div className='neo-event-card-date-day'>{new Date (start_date).toLocaleString(locale, {timeZone: config.event_header.timezone, day: '2-digit'})}</div>
                <div className='neo-event-card-date'>{new Date (start_date).toLocaleString(locale, {timeZone: config.event_header.timezone, month: 'short', year:'numeric'})}</div>
                <div className='neo-event-card-date-mobile'>{new Date (start_date).toLocaleString(locale, {timeZone: config.event_header.timezone, month: 'short'})}</div>
                {display_hours && <div className='neo-event-card-date-hour'>{new Date (start_date).toLocaleString(locale, {timeZone: config.event_header.timezone, hour: '2-digit', minute: '2-digit'})}</div>}
            </div>}
        </div>
    );
}

export default function EventCard({config, onClick=() => {}, listMode = true}) {
    const { t } = useTranslation();
    const [showMore, setShowMore] = React.useState(false);
    const toggleShowMore = React.useCallback(() => setShowMore(showMore => !showMore), []);
    const locale = i18n.language;
    const settings = config.settings || config.event_header.settings;
    const { description, address, end_date, publication_status, sale_start_date, sale_end_date } = config.event_header;
	const {
        display_date = true,
        display_location = true,
        display_name_location = true,
        display_description = true,
        display_finished_events = true,
    } = settings;

    const eventIsOver = React.useMemo(() => {
        if (!end_date) {
            return false;
        }
        return end_date < moment.utc().format();
    }, [end_date]);

    const saleIsOver = React.useMemo(() => {
        if (!sale_end_date) {
            return false;
        }
        return moment.utc(sale_end_date).format() < moment.utc().format();
    }, [sale_end_date]);

    const eventComingSoon = React.useMemo(() => {
        if (!sale_start_date) {
            return false;
        }
        return moment.utc(sale_start_date).format() > moment.utc().format();
    }, [sale_start_date]);

    const full_address = React.useMemo(() => {
        const {address1, address2, name, zip_code, city, country} = address;
        
        const elements = [
            address1,
            address2,
            `${display_name_location ? name : ''}`,
            `${zip_code}${zip_code && city && ' '}${city}`,
            country
        ];
        return elements.filter(a => a).join(', ');
    }, [address, display_name_location]);

    const hasDescription = React.useMemo(() => {
        return Boolean(description);
    }, [description]);

    if (eventIsOver && !display_finished_events) {
        return null;
    }

    if(!['published', 'over'].includes(publication_status)) {
		return null;
	}

    const propsComponentSection = React.useMemo(() => {
        return {
            t,
            locale,
            config,
            settings,
            eventComingSoon,
            eventIsOver,
            saleIsOver,
            hasDescription,
            showMore,
            toggleShowMore,
        };
    }, [t, locale, config, settings, eventComingSoon, eventIsOver, saleIsOver, hasDescription, showMore, toggleShowMore]);

    const _onClick = React.useCallback(() => {
        if (eventIsOver || saleIsOver) {
            return null;
        } else {
            onClick();
        }
    }, [eventIsOver, saleIsOver, onClick]);

    return (
        <div className={classnames('neo-event-card-container', {'card-view': !listMode, 'show-more': showMore, 'is-over': eventIsOver})} onClick={_onClick}>
            <div className={classnames('neo-event-card', {'hide-more': !showMore})}>
                <ImageEvent {...propsComponentSection} />
                <InfosEvent {...propsComponentSection} />
            </div>
            {
                showMore && <div className='neo-event-card-more-informations'>
                    {display_date && <DateInformation {...propsComponentSection} />}
                    {display_location && <div className='neo-info-group-with-icon'>
                        <Icon name='mapPin'/>
                        <div>{full_address}</div>
                    </div>}
                    {hasDescription && display_description &&  <div className={showMore ? 'neo-event-card-description' : ''}>
                        <HtmlContent content={description} />
                    </div>}
                </div>
            }
        </div>
    )
}